import { Component, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { HostService } from '../../services/host.service';
import { LogService } from '../../services/log.service';

// Importaciones de interfaces.
import { IHost } from '../../interfaces/host.interface';

// Importaciones de modelos.
import { Host } from '../../models/host.model';
import { Log } from '../../models/log.model';

/**
 * Componente de diálogo para la creación del host.
 *
 * Este componente proporciona una interfaz para crear un host.
 * Se muestra como un cuadro de diálogo y permite la edición de los datos.
 */
@Component({
  selector: 'app-host-create-dialog',
  templateUrl: './host-create-dialog.component.html',
  standalone: false,
})
export class HostCreateDialogComponent {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de la creación del host.
   */
  private readonly dialogRef: MatDialogRef<HostCreateDialogComponent> = inject(
    MatDialogRef<HostCreateDialogComponent>
  );

  /**
   * Indica si la operación de creado está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se crea el host.
   */
  loading: boolean = false;

  /**
   * Lista de errores.
   *
   * Contiene los mensajes de error generados si la operación de guardado falla.
   */
  errors: string[] = [];

  /**
   * Formulario de actualización del host.
   *
   * Gestiona los campos y validaciones necesarias para crear el host.
   * Se inicializa como un `FormGroup` y contiene los controles correspondientes a los datos editables.
   */
  form: FormGroup = new FormGroup({
    name: new FormControl('', Validators.required),
    type: new FormControl('', Validators.required),
    status: new FormControl(true, Validators.required),
  });

  /**
   * Obtiene el control del formulario para el campo 'name'.
   *
   * Retorna el control asociado a la propiedad 'name' del formulario,
   * que gestiona el nombre del host.
   *
   * @returns El control de formulario para 'name'.
   */
  get formName(): AbstractControl | null {
    return this.form.get('name');
  }

  /**
   * Obtiene el control del formulario para el campo 'type'.
   *
   * Retorna el control asociado a la propiedad 'type' del formulario,
   * que gestiona el tipo del host.
   *
   * @returns El control de formulario para 'type'.
   */
  get formType(): AbstractControl | null {
    return this.form.get('type');
  }

  /**
   * Obtiene el control del formulario correspondiente al campo 'status'.
   *
   * Este control indica si el empleado está deshabilitado o no.
   *
   * @returns El control del formulario asociado al campo 'status', o null si no está disponible.
   */
  get formStatus(): AbstractControl | null {
    return this.form.get('status');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param hostService - Servicio para gestionar la información del host.
   * @param logService - Servicio para gestionar los registros.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private hostService: HostService,
    private logService: LogService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Crear el host.
   *
   * Este método se encarga de realizar el proceso de creado del host.
   * Puede incluir validaciones, llamadas a servicios externos y manejo de errores.
   */
  async create(): Promise<void> {
    this.loading = true;
    this.errors = [];

    try {
      const hostResponse: IHost = await this.hostService.create(
        new Host({
          name: this.formName?.value,
          type: this.formType?.value,
          disabled: Number(!this.formStatus?.value),
        })
      );

      // Añadir un registro de creación del host.
      try {
        await this.logService.create(
          new Log({
            description: `Ha creado el host '${hostResponse.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de creación del host.'
          );
        }
      }

      this.snackBar.open(
        `Has creado correctamente el host ${hostResponse.id}`,
        '',
        {
          duration: 5000,
        }
      );

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loading = false;
  }
}
